import { differenceInCalendarDays } from 'date-fns';
import type { IntlShape } from 'react-intl';
import type {
    ClinicalPeopleList,
    ClinicalTagDto,
    PatientSummaryDto,
    TreatmentSiteDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import type { IntakeBody } from '~/backend/connector-next-api/patientWritesClient';
import { parseWireDate } from '~/utils/date';
import { resolveCourseStage } from './patientListModel';

/** Whether the patient's workflow has not started: its course is still at Received. */
export function workflowNotStarted(patient: {
    courseStage?: string | null;
}): boolean {
    return resolveCourseStage(patient.courseStage) === 'Received';
}

/**
 * The installation's clinical tags and treatment sites, retired ones included.
 * Each is undefined until it has loaded, whatever became of the other.
 */
export type ClinicalLists = {
    tags: readonly ClinicalTagDto[] | undefined;
    sites: readonly TreatmentSiteDto[] | undefined;
};

/**
 * The offered sites a patient's images suggest: every site not retired whose
 * Body Part Examined values include one of the patient's, in list order.
 * Matching ignores case.
 */
export function suggestedSites(
    sites: readonly TreatmentSiteDto[],
    bodyParts: readonly string[] | null | undefined
): TreatmentSiteDto[] {
    const parts = new Set((bodyParts ?? []).map((part) => part.toUpperCase()));
    return sites.filter(
        (site) =>
            !site.retired &&
            (site.bodyParts ?? []).some((part) => parts.has(part.toUpperCase()))
    );
}

/** How many days before its treatment start a patient not yet exported is flagged, as a warning and then as critical. */
export const TREATMENT_START_WARNING = {
    warnDaysBefore: 5,
    criticalDaysBefore: 1,
} as const;

/** How close a treatment start is, against the treatment start warning. */
export type TreatmentStartUrgency =
    | { level: 'none' }
    | { level: 'ok' | 'warn' | 'critical' | 'started'; days: number };

/**
 * How many calendar days remain from `today` before the patient's treatment
 * start ("yyyy-MM-dd"), and whether that falls inside the warning or critical
 * window of {@link TREATMENT_START_WARNING}: started once passed, and none for
 * an exported patient.
 */
export function treatmentStartUrgency(
    patient: { treatmentStart?: string | null; courseStage?: string | null },
    today: Date
): TreatmentStartUrgency {
    const start = parseWireDate(patient.treatmentStart);
    if (start === undefined || patient.courseStage === 'Exported') {
        return { level: 'none' };
    }
    const days = differenceInCalendarDays(start, today);
    if (days < 0) {
        return { level: 'started', days };
    }
    if (days <= TREATMENT_START_WARNING.criticalDaysBefore) {
        return { level: 'critical', days };
    }
    if (days <= TREATMENT_START_WARNING.warnDaysBefore) {
        return { level: 'warn', days };
    }
    return { level: 'ok', days };
}

/** The values a patient's intake is made of. */
export type IntakeValues = {
    siteId: string | undefined;
    radiationOncologistId: string | undefined;
    plannerId: string | undefined;
    treatmentStart: string | undefined;
};

/** A patient as far as its intake goes. */
type IntakePatient = Pick<
    PatientSummaryDto,
    | 'courseStage'
    | 'siteId'
    | 'radiationOncologistId'
    | 'plannerId'
    | 'treatmentStart'
>;

/** The intake values a patient holds. */
export function storedIntake(patient: IntakePatient): IntakeValues {
    return {
        siteId: patient.siteId ?? undefined,
        radiationOncologistId: patient.radiationOncologistId ?? undefined,
        plannerId: patient.plannerId ?? undefined,
        treatmentStart: patient.treatmentStart ?? undefined,
    };
}

/** `values` as the intake write sends them: a value nobody has chosen is null. */
export function intakeBody(values: IntakeValues): IntakeBody {
    return {
        siteId: values.siteId ?? null,
        radiationOncologistId: values.radiationOncologistId ?? null,
        plannerId: values.plannerId ?? null,
        treatmentStart: values.treatmentStart ?? null,
    };
}

/** The name of the user `id`, or undefined when no listed user has it. */
export function personName(
    people: ClinicalPeopleList,
    id: string
): string | undefined {
    return (
        (people.people ?? []).find((person) => person.id === id)?.displayName ??
        undefined
    );
}

/**
 * How the user `id` reads: their name, "…" until the people list has loaded,
 * and "Unknown user" for an id no listed user has.
 */
export function personLabel(
    intl: IntlShape,
    people: ClinicalPeopleList | undefined,
    id: string
): string {
    if (people === undefined) {
        return '…';
    }
    return (
        personName(people, id) ??
        intl.formatMessage({ id: 'patientList.care.unknownPerson' })
    );
}

/** Whoever holds a patient: how they read, and whether they are a listed user. */
export type Assignee = { name: string; listed: boolean };

/** Whoever holds the patient, or undefined when nobody does. */
export function patientAssignee(
    intl: IntlShape,
    people: ClinicalPeopleList | undefined,
    patient: Pick<PatientSummaryDto, 'assignedToUserId'>
): Assignee | undefined {
    const id = patient.assignedToUserId;
    if (!id) {
        return undefined;
    }
    return {
        name: personLabel(intl, people, id),
        listed: people !== undefined && personName(people, id) !== undefined,
    };
}
